/**
 * Handler code for the site wide image recognition search form.
 * This code is site wide so we can't rely on Angular or any other library code.
 */

const ImageDownsampler = require('./image_downsampler.js');
const scriptData = require('./script_data.js');

let _lastTarget = null;

const BROWSE_FILES_LINK_ID = 'drop-file-here__link';
const CLOSE_BUTTON_CLASS = 'visual-search-drop-zone__close';
const DRAG_AND_DROP_FILE_INPUT_ID = 'visual-search-file-input-drag-and-drop';
const DRAG_AND_DROP_FORM_ID = 'visual-search-drag-and-drop-form'; // form id for all pages except vs/upload
const VS_DROP_FILE_FORM_ID = 'visual-search-drop-file-form'; // the form id on the vs/upload page
const VS_DROP_FILE_HERE_ID = 'drop-file-here';
const DRAG_ZONE_ID = 'drop-file-here__upload';
const DROP_ZONE_CLASS = '.visual-search-drop-zone';
const ERROR_TEXT_CLASS = 'visual-search-error__text';
const ERROR_DETAILS_CLASS = 'visual-search-error__details';
const UPLOAD_ANOTHER_FILE_INPUT_ID = 'upload-another-file-input';
const TRY_VISUAL_SEARCH_LINK_ID = 'try-a-visual-search';
const SEARCH_BAR_ID = 'header-search'; // the site-wide search bar (af-search-bar)
const VISUAL_SEARCH_FILE_INPUT_SELECTOR = '.visual-search-file-input';
const VISUAL_SEARCH_UPLOAD_FORM_SELECTOR = '#visual-search-upload-form';

const ALLOWED_FILE_TYPES = ['image/gif', 'image/jpeg', 'image/png', 'image/webp'];
const MAX_FILE_BYTES = 5242880;

// Use ready state DONE code from the XMLHttpRequest object if it's available.
const READY_STATE_DONE = XMLHttpRequest.DONE || 4; // eslint-disable-line no-magic-numbers
const OK_STATUS = 200;

// Translations will loaded during initialization.
let i18n;

export function init() {
  // If the document itself has loaded, we can initialize the event handlers, etc.
  if (document.readyState != 'loading') {
    _initWhenReady();
  // If the document is still in a loading state, initialize it once it has loaded.
  } else {
    document.addEventListener('DOMContentLoaded', _initWhenReady);
  }
}

/**
 * Initialize the page listeners and setup the page to handle the use dragging and
 * dropping images.
 */
function _initWhenReady() {
  i18n = scriptData.getData('/visual_search/i18n');

  if (!_getUploadForm()) {
    return;
  }

  _getImageFileInput(_getUploadForm()).
    addEventListener('change', _handleFileInputChange);

  // The Recommendations page owns its own image-drop flow, so the site-wide
  // visual-search drag listeners must not hijack drops there (camera-icon click
  // search below is unaffected). Keyed off the page element, not the URL, for locales.
  if (!document.querySelector('intent-recommendations-page')) {
    window.addEventListener('dragenter', _handleWindowDragEnter);
    window.addEventListener('dragleave', _handleWindowDragLeave);
    window.addEventListener('dragover', _handleWindowDragOver);
    window.addEventListener('drop', _handleWindowDrop);
  }

  window.addEventListener('af.search-bar.visual-search', function() {
    _getDragAndDropFileInput().addEventListener('change', _handleFileInputChange);
    _handleFileUploadLinkClick();
  });

  // Paste-to-search: listen on the search-bar control itself so a paste made
  // while it is focused starts Visual Search. Paste events are composed, so
  // they reach this listener from the control's inner input.
  const searchBar = _getSearchBar();
  if (searchBar) {
    searchBar.addEventListener('paste', _handlePaste);
  }

  // On the upload page the user can paste an image anywhere, so also listen at
  // the document level there. A paste in the search bar is consumed by the
  // listener above first (it stops propagation), so it is never uploaded twice.
  if (_isVisualSearchUploadPage()) {
    document.addEventListener('paste', _handlePaste);
  }

  _addLinkEventListeners([_getBrowseFilesLink(), _getTryVisualSearchLink()]);

  _initDragAndDropForm();

  // This is called after _initDragAndDropForm() to add the listener to the correct button on the Discover page
  _getCloseButton().addEventListener('click', _handleCloseButtonClick);

  if (_getUploadAnotherFileInput()) {
    _getUploadAnotherFileInput().addEventListener('change', _handleFileInputChange);
  }
}

/*
 * It is possible to start a visual search by clicking a link.
 * Doing so should open a file upload dialog, which requires a listener on the link.
 * If any of these links are present, we need also a listener on the file input
 * that displays the loading screen when a file is uploaded.
 *
 * @private
 * @param {Array} links - An array of HTMLElements (or null) consisting every link on
 *                        the site that would need these listeners.
 */
function _addLinkEventListeners(links) {
  // If an item in links is null, it wasn't found on the page
  const linksOnPage = links.filter(function(e) {
    return e;
  });

  linksOnPage.forEach(function(link) {
    link.addEventListener('click', _handleFileUploadLinkClick);
  });

  if (linksOnPage.length > 0) {
    const input = _getDragZoneFileInput();
    if (input) {
      input.addEventListener('change', _handleFileInputChange);
    }
  }
}

/**
 * Returns the link that opens the dialog to browse for files on your computer
 *
 * @private
 * @returns {Element}
 */
function _getBrowseFilesLink() {
  return document.getElementById(BROWSE_FILES_LINK_ID);
}

/**
 * Returns the button that, if clicked, will close the drag and drop modal and cancel the request
 *
 * @private
 * @returns {Element}
 */
function _getCloseButton() {
  return _getDragAndDropUploadForm().getElementsByClassName(CLOSE_BUTTON_CLASS)[0];
}

/**
 * Returns the file input for the drag and drop element.
 *
 * @private
 * @returns {Element}
 */
function _getDragAndDropFileInput() {
  return document.getElementById(DRAG_AND_DROP_FILE_INPUT_ID);
}

/**
 * Returns the upload form for the drag and drop element.
 *
 * @private
 * @returns {Element}
 */
function _getDragAndDropUploadForm() {
  _toggleDragAndDropUploadFormStyle();
  return _getActiveForm();
}

/**
 * Returns the drag and drop form that should currently be active.
 * Uses the drop-file-here form on the /upload page only.
 * All other scenarios (show page, any step) use the subheader fullscreen form.
 *
 * @private
 * @returns {Element}
 */
function _getActiveForm() {
  const container = document.getElementById(VS_DROP_FILE_HERE_ID);
  if (container && container.getBoundingClientRect().width > 0) {
    return document.getElementById(VS_DROP_FILE_FORM_ID);
  }
  return document.getElementById(DRAG_AND_DROP_FORM_ID);
}

/**
 * Returns the visual search form action.
 *
 * @private
 * @returns {String}
 */
function _getUploadPath() {
  const path = document.getElementById(DRAG_AND_DROP_FORM_ID).action;
  return path + '.json';
}

/**
 * Returns the file input inside the drag zone specified by DRAG_ZONE_ID
 *
 * @private
 * @returns {Element}
 */
function _getDragZoneFileInput() {
  const dragZone = document.getElementById(DRAG_ZONE_ID);
  if (!dragZone) {
    return;
  }

  return dragZone.querySelector(VISUAL_SEARCH_FILE_INPUT_SELECTOR);
}

/**
 * Returns the image drop zone.
 *
 * @private
 * @returns {Element}
 */
function _getDropZone() {
  return _getDragAndDropUploadForm().querySelector(DROP_ZONE_CLASS);
}

/**
 * Returns the element containing error message text.
 *
 * @private
 * @returns {Element}
 */
function _getErrorText() {
  return _getActiveForm().getElementsByClassName(ERROR_TEXT_CLASS)[0];
}

/**
 * Returns the element containing error message details.
 *
 * @private
 * @returns {Element}
 */
function _getErrorDetails() {
  return _getActiveForm().getElementsByClassName(ERROR_DETAILS_CLASS)[0];
}

/**
 * Returns the input element that will handle uploading the image file to
 * recognize.
 *
 * @private
 * @param {Element} parent
 * @returns {Element}
 */
function _getImageFileInput(parent) {
  return parent.querySelector(VISUAL_SEARCH_FILE_INPUT_SELECTOR);
}

function _getTryVisualSearchLink() {
  return document.getElementById(TRY_VISUAL_SEARCH_LINK_ID);
}

/**
 * Returns the site-wide search bar control, or null when it is absent
 * (for example the homepage, which does not render it).
 *
 * @private
 * @returns {Element}
 */
function _getSearchBar() {
  return document.getElementById(SEARCH_BAR_ID);
}

/**
 * Returns the input element inside the "Upload another file" button on the results page
 *
 * @private
 * @returns {Element}
 */
function _getUploadAnotherFileInput() {
  return document.getElementById(UPLOAD_ANOTHER_FILE_INPUT_ID);
}

/**
 * Returns the search form that will submit the actual search.
 *
 * @private
 * @returns {Element}
 */
function _getUploadForm() {
  return document.querySelector(VISUAL_SEARCH_UPLOAD_FORM_SELECTOR);
}

/**
 * Hides the drag and drop modal and clears error text so it isn't displayed on the next search
 *
 * @listens Event
 * @private
 */
function _handleCloseButtonClick() {
  _hideDragAndDropModal();
}

/**
 * Handles change events on the image file input form. Checks the file selected through
 * the dialog opened in _handleBrowseFilesLinkClick and submits the drag and drop form
 * if it is valid.
 *
 * If the input change is in the search bar, this disables the search input, changes
 * the value to file name, and shows the image search button.
 *
 * @listens Event
 * @private
 * @param {Event} e
 */
function _handleFileInputChange(e) {
  _metric('typekit.click.visual-search.browse-select-image');
  _processAndUploadImage(_getImageFileInput(e.target.form).files[0]);
}

/**
 * Opens a dialog to browse files when a file upload link is clicked
 *
 * @listens Event
 * @private
 */
function _handleFileUploadLinkClick() {
  _getImageFileInput(_getUploadForm()).click();
}

/**
 * Report error messages

 * @param response {}
 * @private
 */
function _reportMetricsAndErrors(response) {
  if (response.metric) {
    _metric(response.metric.name, response.metric.value);
  }

  if (response.errorMessage) {
    _displayErrorMessage(response.errorMessage.text, response.errorMessage.details);
  }
}

/**
 * Make an XHR call
 *
 * @param url
 * @param callback
 * @param body
 * @private
 */
function _request(url, callback, body) {
  let req = new XMLHttpRequest();
  req.onreadystatechange = function() {
    if (req.readyState === READY_STATE_DONE && req.status === OK_STATUS) {
      if (req.responseText.trim()) {
        callback(JSON.parse(req.responseText));
      } else {
        callback();
      }
    } else if (req.readyState === READY_STATE_DONE && req.status !== OK_STATUS) {
      _displayErrorMessage(
        i18n.visual_search.something_went_wrong.text,
        i18n.visual_search.something_went_wrong.details);
    }
  };

  _getCloseButton().addEventListener('click', function() {
    _cancelRequest(req);
  });

  req.open('POST', url, true);

  req.setRequestHeader('Content-Type', 'application/json');
  req.setRequestHeader('X-CSRF-Token', _getXsrfToken());
  req.setRequestHeader('X-Requested-With', 'XMLHttpRequest');

  req.send(body);
}

function _getXsrfToken() {
  const metaTags = document.getElementsByTagName('meta');
  for (let i = 0; i < metaTags.length; i++) {
    if (metaTags[i].getAttribute('name') == 'csrf-token') {
      return metaTags[i].getAttribute('content');
    }
  }
}

/**
 * Handles drag enter events on the window.
 *
 * @listens Event
 * @private
 * @param {Event} e
 */
function _handleWindowDragEnter(e) {
  if (!_isFile(e)) {
    return;
  }

  _lastTarget = e.target;

  const dropZone = _getDropZone();

  _show(_getDragAndDropUploadForm());
  dropZone.querySelector('.filename').innerText = '';
  dropZone.classList.remove('loading');
  dropZone.classList.add('active');
}

/**
 * Handles drag leave events on the window.
 *
 * @listens Event
 * @private
 * @param {Event} e
 */
function _handleWindowDragLeave(e) {
  e.preventDefault();

  if (e.target === _lastTarget) {
    _hideDragAndDropModal();
  }
}

/**
 * Handles drag over events on the window.
 *
 * @listens Event
 * @private
 * @param {Event} e
 */
function _handleWindowDragOver(e) {
  e.preventDefault();
  e.stopPropagation();
}

function _initDragAndDropForm() {
  const subheaderForm = document.getElementById(DRAG_AND_DROP_FORM_ID);
  if (subheaderForm) {
    document.body.appendChild(subheaderForm);
  }
}

/**
 * Handles drop events on the window.
 *
 * @listens Event
 * @private
 * @param {Event} e
 */
function _handleWindowDrop(e) {
  e.preventDefault();
  _metric('typekit.click.visual-search.drop-image');
  if (e.dataTransfer.files.length == 1) {
    const file = e.dataTransfer.files[0];

    _getDropZone().classList.remove('active');

    window.setTimeout(function() {
      _processAndUploadImage(file);
    }, 1);
  } else if (e.dataTransfer.files.length > 1) {
    _displayErrorMessage(
      i18n.visual_search.uploading_multiple_files.text,
      i18n.visual_search.uploading_multiple_files.details);
  }
}

/**
 * Handles paste events for visual search. If the clipboard holds an image, the
 * first image file item is uploaded through the normal visual search flow.
 * Default paste behaviour is prevented only when an image is consumed, so text
 * and other non-image pastes continue to behave normally. Propagation is also
 * stopped so a paste caught by both the search-bar and document listeners is
 * only uploaded once, and the focused control is blurred so the search bar's
 * suggestions dropdown does not sit on top of the loading or error overlay.
 *
 * @listens Event
 * @private
 * @param {Event} e
 */
function _handlePaste(e) {
  const items = e.clipboardData.items;

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.kind === 'file' && item.type.startsWith('image/')) {
      e.preventDefault();
      e.stopPropagation();
      _blurFocusedElement();
      _metric('typekit.click.visual-search.paste-image');
      _processAndUploadImage(item.getAsFile());
      return;
    }
  }
}

/**
 * Blurs the control that currently has focus, descending through open shadow
 * roots to reach the real focused element (e.g. the input inside af-search-bar).
 * When an image is pasted into the search bar, blurring it dismisses the
 * suggestions dropdown so it does not overlap the loading or error overlay.
 *
 * @private
 */
function _blurFocusedElement() {
  let active = document.activeElement;
  while (active && active.shadowRoot && active.shadowRoot.activeElement) {
    active = active.shadowRoot.activeElement;
  }
  if (active && typeof active.blur === 'function') {
    active.blur();
  }
}

/**
 * Was the event triggered by a file being dropped onto the page?
 *
 * @private
 * @param {Event} evt
 * @returns {Boolean}
 */
function _isFile(evt) {
  const dt = evt.dataTransfer;

  for (let i = 0; i < dt.types.length; i++) {
    if (dt.types[i] === 'Files') {
      return true;
    }
  }

  return false;
}

/**
 * Is the current page the standalone visual search upload page
 * (/fonts/vs/upload)? Keyed off the path because the drop zone element also
 * renders on the results and error pages, which have their own text inputs.
 *
 * @private
 * @returns {Boolean}
 */
function _isVisualSearchUploadPage() {
  return window.location.pathname.endsWith('/fonts/vs/upload');
}

/**
 * Show the element.
 *
 * @private
 * @param {Element} element
 */
function _show(element) {
  element.style.display = 'block';
}

/**
 * Hide the element.
 *
 * @private
 * @param {Element} element
 */
function _hide(element) {
  element.style.display = 'none';
}

/*
 * Display error message in the region where the file was uploaded
 * (either the visual search modal or the "Drop file here to scan for text" box).
 *
 * @private
 * @param {String} text The error message to display
 * @param {String} details Details about the error message
 */
function _displayErrorMessage(text, details) {
  const dropZone = _getDropZone();
  dropZone.classList.remove('loading');
  dropZone.classList.add('active');

  dropZone.querySelector('.filename').innerText = '';
  _getErrorText().innerHTML = text;
  _getErrorDetails().innerHTML = details;
}

/*
 * Hides the drag and drop modal
 *
 * @private
 *
 * @returns {String}
 */
function _hideDragAndDropModal() {
  _hide(_getDragAndDropUploadForm());
  _resetModalState();

  const dropZone = _getDropZone();
  dropZone.classList.add('loading');
  dropZone.classList.remove('active');
  dropZone.querySelector('.filename').innerText = '';
}

/**
 * Checks if the "Drop file here" box is in the viewport.
 * If so, the drag and drop form is displayed within it.
 * If not, the full screen drag and drop form is displayed.
 *
 * @private
 */
function _toggleDragAndDropUploadFormStyle() {
  if (document.getElementById('drop-file-here-fullscreen') === null) {
    return;
  }

  const container = document.getElementById(VS_DROP_FILE_HERE_ID);
  const rect = container ? container.getBoundingClientRect() : { top: -1, bottom: 0 };
  if (rect.top < 0 || rect.bottom > (window.innerHeight || document.documentElement.clientHeight)) {
    document.getElementById('drop-file-here-fullscreen_frame').classList.add('visual-search-fullscreen__frame');
    document.getElementById('drop-file-here-fullscreen').classList.remove('drop-file-here__drop-zone');
    document.getElementById(VS_DROP_FILE_HERE_ID).classList.remove('drop-file-here__small');
  } else {
    document.getElementById('drop-file-here-fullscreen_frame').classList.remove('visual-search-fullscreen__frame');
    document.getElementById('drop-file-here-fullscreen').classList.add('drop-file-here__drop-zone');
    document.getElementById(VS_DROP_FILE_HERE_ID).classList.add('drop-file-here__small');
  }
}

/*
 * Parses an uploaded image's metadata. Used for recording metrics about the upload.
 *
 * @private
 * @param {File} file
 * @param {Function} cb a callback function invoked with the metadata
 */
function _getImageMetadata(file, cb) {
  const _URL = window.URL || window.webkitURL;
  let img = new Image();
  img.onload = function() {
    cb({
      width: this.naturalWidth,
      height: this.naturalHeight,
      size: file.size,
      format: file.type
    });
  };
  img.src = _URL.createObjectURL(file);
}

/*
 * Sends custom metric data to New Relic
 *
 * @private
 * @param {String} name The name of the page action being recorded
 * @param {Object} attributes an object representing the page action's metadata
 */
function _metric(name, attributes) {
  if (window.newrelic && typeof window.newrelic !== 'undefined') {
    window.newrelic.addPageAction(name, attributes || {});
  }
}

/**
 * Process the image file and upload the file.
 *
 * @private
 * @param {File} file
 */
function _processAndUploadImage(file) {
  _resetModalState();
  _showLoadingState(file);

  ImageDownsampler.downsample(file, MAX_FILE_BYTES, ALLOWED_FILE_TYPES, function(base64String, fileName) {
    _getImageMetadata(file, function(imageMetadata) {
      _metric('typekit.click.visual-search.upload-image', imageMetadata);
    });

    _request(_getUploadPath(), function(data) {
      window.location.href = data.path;
    }, JSON.stringify({
      image_string: base64String,
      image_file_name: fileName
    }));
  }, _reportMetricsAndErrors);
}

/**
 * Display the visual search loading state.
 *
 * NOTE: this code is reusing the existing loading state from the drag and drop
 * handler, but should be refactored so the loading state is independent of the
 * drag and drop form.
 *
 * @private
 * @param {File} file
 */
function _showLoadingState(file) {
  _show(_getDragAndDropUploadForm());

  const dropZone = _getDropZone();
  dropZone.classList.add('loading');
  dropZone.querySelector('.filename').innerText = file.name;
}

/*
  Abort the request and remove its state change handlers

  @private
  @param {request} XmlHttpRequest
 */
function _cancelRequest(request) {
  request.onreadystatechange = null;
  request.abort();
}

function _resetModalState() {
  _getErrorText().innerHTML = '';
  _getErrorDetails().innerHTML = '';
}
